import axios from "axios";
import moment from "moment";
import { RIGHTSLINE_URL } from "tap/config/constants.js";
import { downloadFile } from "./utilities";

const PPT_URL = import.meta.env.VITE_PPT_URL || "http://localhost:8080";
const TIMEOUT_MS = 60000;

/**
 * Parses audit timestamp fields (createdAt, updatedAt, deletedAt) from UTC strings to Date objects.
 *
 * @param {Object} obj - Object with timestamp fields
 * @returns {Object} New object with parsed timestamps
 */
const parseAuditTimestamps = (obj) => {
  if (!obj) return obj;

  return {
    ...obj,
    createdAt: obj.createdAt
      ? moment.utc(obj.createdAt).toDate()
      : obj.createdAt,
    updatedAt: obj.updatedAt
      ? moment.utc(obj.updatedAt).toDate()
      : obj.updatedAt,
    deletedAt: obj.deletedAt
      ? moment.utc(obj.deletedAt).toDate()
      : obj.deletedAt,
  };
};

/**
 * Axios instance configured for PPT Server API calls.
 * Uses the PPT Server base URL (separate from TAP API).
 */
export const pptApiClient = axios.create({
  baseURL: PPT_URL,
  timeout: TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Backend-served redirect into the Xavier UI for an experience. The per-environment
 * Xavier UI host lives in PPT config (not the front end), so this stays a relative path
 * off the PPT base URL. The endpoint is public (under /external/) and returns a 404 page
 * when Xavier linking is disabled (e.g. local). Used by the client-discount drawer's
 * "View in Xavier" link.
 *
 * @param {number|string} experienceId - Xavier experience id from ClientDiscount.xavierExperienceId
 * @returns {string} absolute URL to the PPT redirect endpoint
 */
export const xavierExperienceUrl = (experienceId) =>
  `${PPT_URL}/external/xavier/experiences/${experienceId}`;

/**
 * Direct deep link into the Rightsline UI for a deal (mod4), env from VITE_ENV.
 *
 * @param {number|string} dealId - ClientDiscount.rlDealId
 * @returns {string} absolute Rightsline URL
 */
export const rightslineDealUrl = (dealId) =>
  `${RIGHTSLINE_URL("deal")}${dealId}`;

/**
 * Direct deep link into the Rightsline UI for a catalog item (mod1), env from VITE_ENV.
 *
 * @param {number|string} catalogId - Discount.rightslineCatalogId
 * @returns {string} absolute Rightsline URL
 */
export const rightslineCatalogUrl = (catalogId) =>
  `${RIGHTSLINE_URL("catalog")}${catalogId}`;

/**
 * Direct deep link into the Rightsline UI for a contact (mod2), env from VITE_ENV.
 *
 * @param {number|string} contactId - ClientDiscount.licenseeRlContactId
 * @returns {string} absolute Rightsline URL
 */
export const rightslineContactUrl = (contactId) =>
  `${RIGHTSLINE_URL("contact")}${contactId}`;

/**
 * True when a request failed because it was aborted rather than because the
 * server said no. Callers that supersede their own in-flight requests use this
 * to tell "I replaced this myself" apart from a genuine failure.
 *
 * Lives here so knowledge of the http client's cancellation shape stays in the
 * api layer.
 *
 * @param {*} error - Whatever a rejected request handed back
 * @returns {boolean}
 */
export const isCancelledRequest = (error) => axios.isCancel(error);

// Add request interceptor to attach auth token
pptApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor to handle unauthorized errors or expired tokens
pptApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && [401, 403].includes(error.response.status)) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

/**
 * Fetches the current authenticated user's PPT context from the PPT Server.
 *
 * @returns {Promise<{id: string, email: string, displayName: string, permissions: number[]}>}
 * @throws {Error} If the request fails (network error, 401, 403, etc.)
 */
export async function fetchCurrentUser() {
  const response = await pptApiClient.get("/api/me");
  return response.data;
}

export const fetchPromosList = async () => {
  const response = await pptApiClient.get("/api/v1/promotions");
  const { data, total } = response.data;
  const parsedData = data.map((promo) => ({
    ...promo,
    // Parse dates to Date objects in the local timezone
    startDate: promo.startDate ? moment(promo.startDate).toDate() : null,
    endDate: promo.endDate ? moment(promo.endDate).toDate() : null,
  }));
  return { data: parsedData, total };
};

const parsePromotionResponse = (response) => {
  if (response.data) {
    response.data = {
      ...response.data,
      startDate: response.data.startDate
        ? moment(response.data.startDate).toDate()
        : null,
      endDate: response.data.endDate
        ? moment(response.data.endDate).toDate()
        : null,
    };
  }
  if (response.data?.discounts) {
    response.data.discounts = response.data.discounts.map((discount) => ({
      ...discount,
      startDate: discount.startDate
        ? moment(discount.startDate).toDate()
        : null,
      endDate: discount.endDate ? moment(discount.endDate).toDate() : null,
    }));
  }
  return response;
};

export const putPromotionStage = async (promotionId, stageId) => {
  const response = await pptApiClient.put(
    `/api/v1/promotions/${promotionId}/stage`,
    { stageId },
  );
  return parsePromotionResponse(response);
};

export const completePromotionStage = async (promotionId, stageId) => {
  const response = await pptApiClient.post(
    `/api/v1/promotions/${promotionId}/stage/complete`,
    { stageId },
  );
  return parsePromotionResponse(response);
};

export const sendBackPromotionStage = async (promotionId, stageId, reason) => {
  const response = await pptApiClient.post(
    `/api/v1/promotions/${promotionId}/stage/back`,
    { stageId, reason },
  );
  return parsePromotionResponse(response);
};

export const updatePromotionOwner = async (promotionId, owner) => {
  const response = await pptApiClient.put(
    `/api/v1/promotions/${promotionId}/owner`,
    { owner },
  );
  return parsePromotionResponse(response);
};

export const updatePromotionAssignee = async (promotionId, assignee) => {
  const response = await pptApiClient.put(
    `/api/v1/promotions/${promotionId}/assignee`,
    { assignee },
  );
  return parsePromotionResponse(response);
};

/**
 * Operator-triggered re-evaluation of every assembly issue against current
 * discount + ClientDiscount state. Returns the refreshed PromotionDetail
 * response so callers can swap it into store without a follow-up GET.
 */
export const revalidatePromotionIssues = async (promotionId) => {
  const response = await pptApiClient.post(
    `/api/v1/promotions/${promotionId}/reassemble`,
  );
  return parsePromotionResponse(response);
};

/**
 * Operator-triggered re-fetch of every TDRO already linked to this promotion.
 * The scheduled enrichment worker drains the queue at its normal pace; this
 * call only schedules the work. Returns `{ requeuedCount }`.
 */
export const resyncPromotionEnrichment = async (promotionId) => {
  const response = await pptApiClient.post(
    `/api/v1/promotions/${promotionId}/enrichment/resync`,
  );
  return response.data;
};

/**
 * Admin recovery action: flip every FAILED enrichment_queue row back to
 * PENDING so the worker picks them up again. System-wide (no promotion
 * scope). Used after a Rightsline outage where entries exhausted their
 * retry budget while the upstream was unavailable. Returns `{ retriedCount }`.
 */
export const retryFailedEnrichment = async () => {
  const response = await pptApiClient.post(
    `/api/v1/admin/enrichment-queue/retry-failed`,
  );
  return response.data;
};

export const fetchPromotion = async (promotionId) => {
  const response = await pptApiClient.get(`/api/v1/promotions/${promotionId}`);
  return parsePromotionResponse(response);
};

export const fetchPromotionDiscounts = async (promotionId) => {
  const response = await pptApiClient.get(
    `/api/v1/promotions/${promotionId}/discounts`
  );

  // Parse discount dates
  if (response.data?.discounts) {
    response.data.discounts = response.data.discounts.map((discount) => ({
      ...discount,
      startDate: discount.startDate
        ? moment(discount.startDate).toDate()
        : null,
      endDate: discount.endDate ? moment(discount.endDate).toDate() : null,
    }));
  }

  return response.data;
};

export const fetchPromotionEvents = async (
  promotionId,
  {
    offset = 0,
    limit = 50,
    sortBy = null,
    sortOrder = null,
    filterBy = null,
  } = {},
) => {
  const params = { offset, limit };
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;
  if (filterBy) params.filterBy = filterBy;

  const response = await pptApiClient.get(
    `/api/v1/promotions/${promotionId}/events`,
    { params },
  );
  const data = response.data;
  if (data?.events) {
    // Parse event timestamps to Date objects in UTC
    data.events = data.events.map((event) => ({
      ...event,
      occurredAt: event.occurredAt
        ? moment.utc(event.occurredAt).toDate()
        : null,
    }));
  }
  return data;
};

export const fetchPromotionIssues = async (promotionId) => {
  const response = await pptApiClient.get(
    `/api/v1/promotions/${promotionId}/issues`,
  );
  const data = response.data;

  // Helper to parse issue timestamps in UTC
  const parseIssueTimestamps = (issues) =>
    (issues || []).map((issue) => ({
      ...issue,
      createdAt: issue.createdAt ? moment.utc(issue.createdAt).toDate() : null,
    }));

  // Parse promotion-level issues
  if (data?.promotionLevel) {
    data.promotionLevel = data.promotionLevel.map((typeGroup) => ({
      ...typeGroup,
      issues: parseIssueTimestamps(typeGroup.issues),
    }));
  }

  return data;
};

export const fetchPromotionDeliveries = async (promotionId) => {
  const response = await pptApiClient.get(
    `/api/v1/promotions/${promotionId}/deliveries`,
  );
  return response.data;
};

export const fetchDiscountDetails = async (discountId) => {
  const response = await pptApiClient.get(`/api/v1/discounts/${discountId}`);
  let data = response.data;

  // Helper to parse issue timestamps in UTC
  const parseIssueTimestamps = (issues) =>
    (issues || []).map((issue) => ({
      ...issue,
      createdAt: issue.createdAt ? moment.utc(issue.createdAt).toDate() : null,
    }));

  // Parse discount audit timestamps
  data = parseAuditTimestamps(data);

  // Parse issues grouped by type (nested under issues property)
  if (data?.issues?.byType) {
    data.issues.byType = data.issues.byType.map((type) => ({
      ...type,
      issues: parseIssueTimestamps(type.issues),
    }));
  }

  // Parse audit-event timestamps (UTC) — drives the drawer timeline.
  if (Array.isArray(data?.auditEvents)) {
    data.auditEvents = data.auditEvents.map((event) => ({
      ...event,
      occurredAt: event.occurredAt
        ? moment.utc(event.occurredAt).toDate()
        : null,
    }));
  }

  return data;
};

export const updateDiscountActiveStatus = async (discountId, active) => {
  const response = await pptApiClient.put(
    `/api/v1/discounts/${discountId}/active`,
    { active },
  );

  // Parse discount audit timestamps
  return parseAuditTimestamps(response.data);
};

/**
 * Fetches delivery notes for a specific delivery (clientKey within a promotion).
 *
 * @param {string} promotionId - Promotion ID
 * @param {string|null} clientKey - Client key (null for "Unassigned" delivery)
 * @returns {Promise<{notes: Array}>} Notes array with timestamps parsed to Date objects
 */
export const fetchDeliveryNotes = async (promotionId, clientKey) => {
  // Convert null clientKey to "unassigned" URL segment
  const key = clientKey ?? "unassigned";
  const response = await pptApiClient.get(
    `/api/v1/promotions/${promotionId}/deliveries/${encodeURIComponent(key)}/notes`,
  );

  const data = response.data;
  // Parse note timestamps to Date objects in UTC
  if (data?.notes) {
    data.notes = data.notes.map((note) => ({
      ...note,
      createdAt: note.createdAt ? moment.utc(note.createdAt).toDate() : null,
    }));
  }
  return data;
};

/**
 * Creates a new delivery note.
 *
 * @param {string} promotionId - Promotion ID
 * @param {string|null} clientKey - Client key (null for "Unassigned" delivery)
 * @param {string} noteText - Note text content
 * @returns {Promise<Object>} Created note with timestamp parsed to Date object
 */
export const createDeliveryNote = async (promotionId, clientKey, noteText) => {
  // Convert null clientKey to "unassigned" URL segment
  const key = clientKey ?? "unassigned";
  const response = await pptApiClient.post(
    `/api/v1/promotions/${promotionId}/deliveries/${encodeURIComponent(key)}/notes`,
    { noteText },
  );

  const note = response.data;
  // Parse createdAt timestamp to Date object in UTC
  if (note?.createdAt) {
    note.createdAt = moment.utc(note.createdAt).toDate();
  }
  return note;
};

// File download operations - these use the downloadFile utility from ppt/utilities
// which handles the file-saver integration and Content-Disposition header parsing

/**
 * Export promotion discounts to Excel.
 * Returns a workbook with two sheets: "Discounts" and "Client Discounts".
 *
 * @param {string} promotionId - Promotion ID
 * @param {string} [filename] - Optional filename (default will be extracted from Content-Disposition header)
 * @returns {Promise<void>}
 */
export const exportPromotionDiscounts = async (promotionId, filename) => {
  await downloadFile(
    pptApiClient,
    `/api/v1/promotions/${promotionId}/export/discounts`,
    filename || `promotion-${promotionId}-discounts.xlsx`,
  );
};

/**
 * Export promotion audit log to Excel.
 *
 * @param {string} promotionId - Promotion ID
 * @param {string} [filename] - Optional filename (default will be extracted from Content-Disposition header)
 * @returns {Promise<void>}
 */
export const exportPromotionAuditLog = async (promotionId, filename) => {
  await downloadFile(
    pptApiClient,
    `/api/v1/promotions/${promotionId}/export/audit-log`,
    filename || `promotion-${promotionId}-audit-log.xlsx`,
  );
};

/**
 * Download delivery forms for a specific client or unassigned delivery.
 *
 * @param {string} promotionId - Promotion ID
 * @param {string|null} clientKey - Client key (null or "unassigned" for unassigned delivery)
 * @param {string} [filename] - Optional filename (default will be extracted from Content-Disposition header)
 * @returns {Promise<void>}
 */
export const downloadDeliveryForms = async (
  promotionId,
  clientKey,
  filename,
) => {
  const key = clientKey ?? "unassigned";
  await downloadFile(
    pptApiClient,
    `/api/v1/promotions/${promotionId}/forms?client=${encodeURIComponent(key)}`,
    filename || `delivery-${key}-forms.xlsx`,
  );
};

/**
 * Download all delivery forms for a promotion as a ZIP archive.
 *
 * @param {string} promotionId - Promotion ID
 * @param {string} [filename] - Optional filename (default will be extracted from Content-Disposition header)
 * @returns {Promise<void>}
 */
export const downloadAllDeliveryForms = async (promotionId, filename) => {
  await downloadFile(
    pptApiClient,
    `/api/v1/promotions/${promotionId}/forms`,
    filename || `promotion-${promotionId}-forms.zip`,
  );
};

/**
 * Fetches current user's grid column preferences.
 * Returns consistent shape with all known grid keys (null if not set).
 *
 * @param {Object} [config] - Axios request config. Callers pass a `timeout`
 *   here to override the client's 60s default; grids don't render until this
 *   settles, so the loader wants to give up far sooner than that.
 * @returns {Promise<{
 *   promosGrid: Array|null,
 *   discountDetailsDiscounts: Array|null,
 *   discountDetailsClientDiscounts: Array|null,
 *   promoListDefaultFilters: Object|null
 * }>}
 */
export const fetchGridPreferences = async (config) => {
  const response = await pptApiClient.get(
    "/api/v1/ppm-user-preferences",
    config,
  );
  return response.data;
};

// URL path segment for each grid's preference, keyed by its storage key (see
// COLUMN_STATE_STORAGE_KEYS).
const GRID_PREFERENCE_PATHS = Object.freeze({
  promosGrid: "promos-grid",
  discountDetailsDiscounts: "discount-details-discounts",
  discountDetailsClientDiscounts: "discount-details-client-discounts",
});

/**
 * Replaces the saved column state for one grid. The other grids are untouched.
 *
 * @param {string} gridKey - The grid's storage key, e.g. "promosGrid"
 * @param {Array|null} columnState - The grid's column state, or null to clear it
 * @param {Object} [config] - Axios request config. Callers pass a `signal` here
 *   to abandon a save that a newer one has superseded — two writes to the same
 *   key are ordered by arrival at the server, not by when they were sent, so an
 *   overtaken request has to be cancelled rather than merely ignored.
 * @returns {Promise<void>}
 */
export const updateGridPreferences = async (gridKey, columnState, config) => {
  const path = GRID_PREFERENCE_PATHS[gridKey];
  if (!path) throw new Error(`Unknown grid preference key: ${gridKey}`);

  await pptApiClient.put(
    `/api/v1/ppm-user-preferences/${path}`,
    columnState,
    config,
  );
};

/**
 * Replaces the current user's promo list default filters.
 *
 * @param {Object<string, string>} filters - The promo list's URL query params
 *   (e.g. `{ q: "summer", tile: "errors" }`). An empty object clears the defaults.
 * @returns {Promise<void>}
 */
export const savePromoListDefaultFilters = async (filters) => {
  await pptApiClient.put(
    "/api/v1/ppm-user-preferences/promo-list-default-filters",
    filters,
  );
};
